/**
 * One work by id, and the owned-works search the viewing-room picker uses.
 */
import type {
  CorrectWorkRequest,
  CorrectWorkResponse,
  MoveWorkRequest,
  MoveWorkResponse,
  PublicWorkViewResponse,
  PublishableWorksResponse,
  RemoveWorkResponse,
  SetEstateIdentityHiddenRequest,
  SetEstateRecordAccessRequest,
  SetWorkAttributionPublicRequest,
  SetWorkDisclosureRuleRequest,
  SetWorkDisclosureRuleResponse,
  SetWorkLocationPublicRequest,
  SetWorkVisibilityRequest,
  WorkAttributionVisibilityResponse,
  WorkCorrectionField,
  WorkDisclosureResponse,
  WorkEstateDisclosureResponse,
  WorkRecordResponse,
  WorkVisibility,
  WorkVisibilityResponse,
  WorkWitnessResponse,
} from "@ondo/shared";

import { request, requestWith } from "./request.ts";

/**
 * One work by id, and its collection when the caller may know.
 *
 * The door every relation that is NOT the collection comes through.
 * `fetchCollectionWorks` reads `ondo.works_in()`, which selects from
 * `ondo.work_placements` — so an agent, a work-grant viewer and an attributed
 * artist are all invisible to it, which is why the agent workspace's "Open
 * record" link used to land on a dead page.
 *
 * 404 covers "no such work" and "not yours" identically, so `describeError`
 * renders whichever sentence the server chose and this function draws no
 * distinction of its own — and since OND-68 that is true of the AUDIT door as
 * well, which refuses an unreadable work with the same sentence before writing
 * anything about the attempt.
 *
 * `actingAs` CHANGES NOTHING ABOUT WHAT COMES BACK. `ondo.work_record()` never
 * sees it and the read is decided by `works_read` exactly as before; all it
 * does is say which of the reader's own firms the access row belongs to, so a
 * studio's administration can see a colleague's reads rather than only their
 * own. Pass the open workspace's organisation whenever there is one — a read
 * taken with no acting firm is recorded as the reader's own and is invisible to
 * the firm, which is right for a private collector and a gap for a seat.
 */
/**
 * Correct one catalogue field — OND-41. `POST /v1/works/:id/corrections`.
 *
 * NOT AN EDIT. The server writes a row saying what the record said and what it
 * says now, and the next `fetchWork` carries it back in `corrections`, which is
 * what makes the screen mark the value as corrected rather than print it as
 * plain fact. A 403 is the one sentence for "not the owner and not the estate";
 * a 422 is "that is already what the record says" or a blank title.
 */
export const correctWork = (workId: string, field: WorkCorrectionField, value: string | null) =>
  request<CorrectWorkResponse>(`/works/${workId}/corrections`, {
    field,
    value,
  } satisfies CorrectWorkRequest);

export const fetchWork = (workId: string, actingAs: string | null = null) =>
  request<WorkRecordResponse>(
    `/works/${workId}${actingAs ? `?actingAs=${encodeURIComponent(actingAs)}` : ""}`,
  );

/**
 * The works this caller may put in a viewing room, searched and paged.
 *
 * Narrower than "works I can see": a collection somebody shared with you is
 * theirs to show, and `ondo.create_viewing_room` refuses those outright. The
 * picker has to offer exactly what the server will accept, or it is offering a
 * refusal — `ondo.search_owned_works` is scoped by the same relation, and
 * rls.sh asserts the two cannot drift.
 *
 * Returns the whole envelope rather than unwrapping to `.works` as every other
 * read here does: with paging, the total and the clamped window ARE the answer.
 */
export const searchPublishableWorks = (query: string, limit: number, offset: number) =>
  request<PublishableWorksResponse>(
    `/works/publishable?q=${encodeURIComponent(query)}` +
      `&limit=${String(limit)}&offset=${String(offset)}`,
  );

/**
 * Hide or show the owner's name to the attributed estate — OND-58.
 *
 * TWO FUNCTIONS AND NOT ONE WITH A FLAG, matching the two routes and the two
 * SQL doors. Naming the owner and opening the record are distinct controls, and
 * a single caller taking both would be the first place they became one setting.
 *
 * Both answer with the effective state of BOTH controls, because the server
 * writes one row carrying both — so a screen that changed one never has to
 * guess at the other, and the two cannot drift apart in local state.
 */
export const setEstateIdentityHidden = (workId: string, hidden: boolean) =>
  requestWith<WorkEstateDisclosureResponse>(
    "PUT",
    `/works/${workId}/estate-visibility`,
    { hidden } satisfies SetEstateIdentityHiddenRequest,
  ).then((body) => body.estateDisclosure);

/**
 * Open or close the work's whole record — its history, its documents and its
 * location — to the attributed estate. OND-58.
 *
 * A 403 means the caller does not own the work, and covers "no such work" too:
 * the server gives one refusal for both, so nothing here distinguishes them.
 */
export const setEstateRecordAccess = (workId: string, granted: boolean) =>
  requestWith<WorkEstateDisclosureResponse>(
    "PUT",
    `/works/${workId}/estate-access`,
    { granted } satisfies SetEstateRecordAccessRequest,
  ).then((body) => body.estateDisclosure);

/**
 * Set how far the work travels — OND-47.
 *
 * ONE CALL FOR THREE RUNGS, unlike the two above: those are independent
 * controls and this is one ordered ceiling, so the act carries the setting
 * rather than there being an act per setting.
 *
 * A 403 means the caller does not own the work, and covers "no such work" and
 * "you own it at the wrong rung" as well — one refusal for all three, so
 * nothing here distinguishes them. A 400 means the body named a setting that
 * does not exist; since OND-172 `public` is not one of those.
 *
 * IT CANNOT PUBLISH THE LOCATION, which is the whole reason there is a second
 * function below rather than a second field here. The server carries the
 * location opt-in through this write unchanged, so the answer coming back may
 * report `locationPublic: true` on a work that has just become public — that is
 * a choice the owner made at the other control, not something this one did.
 */
export const setWorkVisibility = (workId: string, visibility: WorkVisibility) =>
  requestWith<WorkVisibilityResponse>(
    "PUT",
    `/works/${workId}/visibility`,
    { visibility } satisfies SetWorkVisibilityRequest,
  ).then((body) => body.visibility);

/**
 * Whether a public view says where the work is kept — OND-172.
 *
 * A SECOND FUNCTION AND NOT A SECOND FIELD ABOVE, which is the ticket's own
 * requirement: "turning a work public must never turn its location public". One
 * caller that could do both is one caller a screen can get wrong, and the
 * warning this control carries has nowhere to sit on a call that is also doing
 * something else.
 *
 * It answers with the whole panel, like the call above, so a screen adopts one
 * shape either way — including `hasLocation`, which is how the panel can say
 * that the opt-in is on and publishing nothing because no location was recorded.
 */
export const setWorkLocationPublic = (workId: string, locationPublic: boolean) =>
  requestWith<WorkVisibilityResponse>(
    "PUT",
    `/works/${workId}/location-visibility`,
    { locationPublic } satisfies SetWorkLocationPublicRequest,
  ).then((body) => body.visibility);

/**
 * The attributed estate says whether its artist's name may be published —
 * OND-172.
 *
 * THE ONLY WRITE IN THIS FILE AN OWNER IS REFUSED. OND-172 divides a work's
 * public view between two parties controlling non-overlapping fields, so there
 * is no precedence rule anywhere — and the shape of that in a client is two
 * functions, each refused to the other's party.
 *
 * A 403 covers "no such work", "that work is not attributed to you" and "wrong
 * rung" in one message, and nothing here distinguishes them.
 */
export const setWorkAttributionPublic = (workId: string, attributionPublic: boolean) =>
  requestWith<WorkAttributionVisibilityResponse>(
    "PUT",
    `/works/${workId}/attribution-visibility`,
    { attributionPublic } satisfies SetWorkAttributionPublicRequest,
  ).then((body) => body.attributionControls);

/**
 * A work as somebody with no relation to it sees it — OND-172.
 *
 * Title, artist, year, medium and one photograph. Not the owner, not a price,
 * not the history. A 404 means the work does not exist OR nobody has published
 * it, identically — "if nothing then nothing", and an answer that told the two
 * apart would be one bit of existence per guessed id.
 *
 * `imagePath` is a path and not a URL: the reader holds a session and signs it
 * themselves through `signWorkThumbnail`, against a storage rule that admits
 * that one object and no other in the work's folder.
 *
 * WHAT TO PRINT UNDER IT is `PUBLIC_WORK_VIEW_NOTICE` from `@ondo/shared` —
 * "Nothing further has been published." One sentence for every screen that
 * renders this, so two of them cannot say different things about one silence.
 */
export const fetchPublicWork = (workId: string) =>
  request<PublicWorkViewResponse>(`/works/${workId}/public`).then(
    (body) => body.publicWork,
  );

/**
 * Who sees which line of this work's history — OND-79.
 *
 * THREE CALLS ON ONE PATH and not a route per audience: a rule's key is
 * (audience, entry) and both halves are nullable, so a path per audience could
 * not express the default. The key is in the BODY because it is data the owner
 * is stating, not a resource they are addressing.
 *
 * EVERY ONE OF THE THREE ANSWERS THE WHOLE PANEL, so the caller adopts what
 * came back rather than guessing what the write did. That matters most for a
 * delegated agent: their rule only ever NARROWS, so what is in force is the
 * union of theirs and the owner's and the write's own row could not show it.
 *
 * A 403 covers every way of not being allowed — not the owner, the wrong rung,
 * an agent with no delegation, a lapsed mandate, and an agent reaching for the
 * owner's own rule. One refusal, so nothing here distinguishes them.
 */
export const fetchWorkDisclosure = (workId: string) =>
  request<WorkDisclosureResponse>(`/works/${workId}/disclosure`);

export const setWorkDisclosureRule = (
  workId: string,
  body: SetWorkDisclosureRuleRequest,
) =>
  requestWith<SetWorkDisclosureRuleResponse>("PUT", `/works/${workId}/disclosure`, body);

/** Takes the RULE and not the key: see 20261005099000 decision 24. */
export const clearWorkDisclosureRule = (workId: string, ruleId: string) =>
  requestWith<WorkDisclosureResponse>(
    "DELETE",
    `/works/${workId}/disclosure/${ruleId}`,
  );

/**
 * The witness for a work — OND-53. Every state of every line the caller may
 * read, with the material to recompute each. Unwrapped to the list.
 */
export const fetchWorkWitness = (workId: string) =>
  request<WorkWitnessResponse>(`/works/${workId}/witness`).then((body) => body.leaves);

/**
 * Move the work into another of the caller's collections — OND-22.
 *
 * The PLACEMENT is the resource, so this is a PUT on it and not a POST on the
 * work: the work is one row for ever and what changes is which collection holds
 * it. A 403 is the only refusal and it covers four things at once — the caller
 * does not own the holding collection, does not own the receiving one, the
 * receiving one does not exist, or the work has been sold and awaits its buyer
 * (OND-17). The last carries the database's own sentence; the panel shows it.
 */
export const moveWork = (workId: string, collectionId: string) =>
  requestWith<MoveWorkResponse>(
    "PUT",
    `/works/${workId}/placement`,
    { collectionId } satisfies MoveWorkRequest,
  );

/**
 * Take the work out of its collection — OND-22. Ends the placement and deletes
 * nothing; the record and its history survive. After this the caller may no
 * longer be able to open the work at all, which is why the screen leaves it
 * rather than reloading it.
 */
export const removeWorkFromCollection = (workId: string) =>
  requestWith<RemoveWorkResponse>("DELETE", `/works/${workId}/placement`);
